// Routing is deliberately limited to the approved pilot, never a phone/name.
export const DURABLE_PILOT_ID = "con_cdda82ce4b9f6cf8eda3fa02eae7eaaa";
export const PILOT_PAGE_LIMIT = 50;

export const PERSISTED_CONVERSATION_FIELDS = new Set([
  "id", "projection", "version", "conversation_id", "conversation_type", "channel",
  "channel_account", "channel_account_id", "participant", "lifecycle_status", "blocked",
  "viewer_state", "assignment", "tags", "last_message", "last_activity_at", "created_at",
  "updated_at", "service_window", "attribution", "automation", "topic",
]);

export function eventConversationId(event, state) {
  const resource = event?.data?.resource;
  if (event?.resource_type === "conversation") return event.resource_id || resource?.id;
  if (resource?.conversation_id) return resource.conversation_id;
  if (event?.resource_type === "message") return state?.messages?.[event.resource_id]?.conversation_id;
  if (resource?.message_id) return state?.messages?.[resource.message_id]?.conversation_id;
  if (event?.resource_type === "attachment") {
    return Object.values(state?.messages || {}).find(item => item.content?.attachment?.id === event.resource_id)?.conversation_id;
  }
  if (event?.resource_type === "participant" && resource?.id === state?.conversations?.[DURABLE_PILOT_ID]?.participant?.id) return DURABLE_PILOT_ID;
  return null;
}

export function pilotContractError(message) {
  return Object.assign(new Error(message), { code: "MESSAGING_RECOVERY_EVENT_INVALID" });
}

export function validatePilotSnapshot(snapshot) {
  if (snapshot?.conversation?.id !== DURABLE_PILOT_ID
    || !["prepared", "active"].includes(snapshot?.recovery?.state)
    || snapshot.recovery.scope !== "conversation" || !snapshot.recovery.cursor
    || typeof snapshot.recovery.cursor !== "string"
    || !Array.isArray(snapshot.messages?.items)
    || snapshot.messages.items.some(item => !item?.id || item.conversation_id !== DURABLE_PILOT_ID)
    || typeof snapshot.messages.page?.has_more !== "boolean"
    || snapshot.messages.page.pagination_source !== "local_store"
    || (snapshot.messages.page.has_more && !snapshot.messages.page.next_cursor)
    || typeof snapshot.messages.history?.complete !== "boolean") {
    throw pilotContractError("Invalid durable pilot snapshot. Existing state was retained.");
  }
}

export function partialHistoryLabel(history) {
  return history?.complete === true ? "Complete local history" : "Partial local history — earlier messages may not be imported yet.";
}

const MESSAGE_PARITY_FIELDS = new Set(["direction", "kind", "sender", "content", "status", "reactions", "context", "failure"]);
const CONVERSATION_PARITY_FIELDS = new Set(["assignment", "viewer_state", "last_message", "lifecycle_status", "blocked", "participant", "tags", "service_window"]);
const PROJECTION_METADATA_KEYS = new Set(["projection", "version", "created_at", "updated_at"]);
const PROJECTION_METADATA_PATHS = new Set(["assignment.source", "assignment.assigned_at"]);
const SHARED_OBJECT_FIELDS = {
  sender: new Set(["id", "actor_type", "user_id", "display_name", "identities"]),
  "sender.identities": new Set(["phone", "username", "bsuid"]),
  assignment: new Set(["assigned_to"]),
  "assignment.assigned_to": new Set(["id", "user_id", "display_name"]),
  viewer_state: new Set(["unread_count", "starred"]),
  last_message: new Set(["id", "kind", "text", "occurred_at", "direction", "status", "sender"]),
};
const isObject = value => value !== null && typeof value === "object" && !Array.isArray(value);
const owns = (value, key) => Object.prototype.hasOwnProperty.call(value, key);

// Compare the shared canonical surface. Extra fields are retained as separate
// projection diagnostics, not silently dropped or counted as semantic failures.
function compareResource(visible, snapshot, resourceType, resourceId, differences, metadataDifferences) {
  const fields = resourceType === "message" ? MESSAGE_PARITY_FIELDS : CONVERSATION_PARITY_FIELDS;
  const record = (path, left, right, category) => {
    (category ? metadataDifferences : differences).push({ resourceType, resourceId,
      messageId: resourceType === "message" ? resourceId : null, path,
      visibleValue: left, snapshotValue: right, ...(category ? { category } : {}) });
  };
  function walk(left, right, path, category = null) {
    if (Object.is(left, right)) return;
    if (isObject(left) && isObject(right)) {
      [...new Set([...Object.keys(left), ...Object.keys(right)])].sort().forEach(key => {
        const childPath = path ? `${path}.${key}` : key;
        const childCategory = category || ((!path && !fields.has(key)) || (SHARED_OBJECT_FIELDS[path] && !SHARED_OBJECT_FIELDS[path].has(key)) || PROJECTION_METADATA_KEYS.has(key)
          || PROJECTION_METADATA_PATHS.has(childPath) ? "projection_metadata" : null);
        if (!owns(left, key) || !owns(right, key)) {
          record(childPath, left[key], right[key], childCategory || "projection_only");
        } else walk(left[key], right[key], childPath, childCategory);
      });
      return;
    }
    if (Array.isArray(left) && Array.isArray(right)) {
      // Reactions are a set of canonical reaction identities, not display order.
      const identified = items => items.every(item => item?.id != null)
        && new Set(items.map(item => item.id)).size === items.length;
      if (path === "reactions" && identified(left) && identified(right)) {
        const leftById = new Map(left.map(item => [item.id, item]));
        const rightById = new Map(right.map(item => [item.id, item]));
        [...new Set([...leftById.keys(), ...rightById.keys()])].sort().forEach(id =>
          walk(leftById.get(id), rightById.get(id), `${path}[id=${JSON.stringify(id)}]`, category));
      } else {
        // Preserve order for content arrays (contacts, options, template parts).
        for (let index = 0; index < Math.max(left.length, right.length); index++) {
          walk(left[index], right[index], `${path}[${index}]`, category);
        }
      }
      return;
    }
    record(path, left, right, category);
  }
  walk(visible, snapshot, "");
}

// Diagnostic only: compare equivalent IDs, not counts from unequal page windows.
export function comparePilotSnapshot(snapshot, state) {
  if (!snapshot) return null;
  const differences = [], metadataDifferences = [];
  const visible = state.conversations[snapshot.conversation.id];
  if (visible) compareResource(visible, snapshot.conversation, "conversation", snapshot.conversation.id, differences, metadataDifferences);
  const matched = snapshot.messages.items.filter(item => state.messages[item.id]);
  matched.forEach(item => compareResource(state.messages[item.id], item, "message", item.id, differences, metadataDifferences));
  return { differences, metadataDifferences, conversationNotLoaded: !visible,
    conversationFields: [...new Set(differences.filter(item => item.resourceType === "conversation").map(item => item.path.split(".")[0]))],
    comparableMessages: matched.length,
    differentMessages: [...new Set(differences.filter(item => item.resourceType === "message").map(item => item.messageId))],
    notLoadedMessages: snapshot.messages.items.length - matched.length };
}
